import { ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ACCESS_LEVELS, ACCESS_ROLES, type AccessChoice, fieldLevels, levelText, roleLabel,
} from "@/lib/forms";
import type { AccessLevel, AccessRole } from "@/lib/types";

/**
 * Who may see and edit a folder or a field (ACL-255's `form_access`, ADR-087 in `platform`): a row per
 * role. Admins always edit and take no rule, so their row is fixed. A folder's level is what its fields
 * get unless a field says otherwise; a field's "Same as folder" follows the folder, and names the level
 * that gives, so the admin sees what a rep will get without opening the folder.
 */
export function AccessEditor(
  props:
    | {
        kind: "folder";
        value: Record<AccessRole, AccessLevel>;
        onChange: (next: Record<AccessRole, AccessLevel>) => void;
        idPrefix: string;
      }
    | {
        kind: "field";
        value: Record<AccessRole, AccessChoice>;
        onChange: (next: Record<AccessRole, AccessChoice>) => void;
        /** The folder's level for each role, which "Same as folder" gives. */
        folder: Record<AccessRole, AccessLevel>;
        idPrefix: string;
        /** One of the platform's own fields: only "Read only" is offered (see `fieldLevels`), and the
         *  note says what these settings reach. */
        platform?: boolean;
      },
) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-text">Who can see and edit</legend>
      <div className="divide-y divide-border rounded-md border border-border">
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-sm text-text">
            <ShieldCheck className="size-3.5 text-text-subtle" aria-hidden />
            Admins
          </span>
          <Badge tone="neutral" size="sm">Always can edit</Badge>
        </div>
        {ACCESS_ROLES.map((role) => {
          const id = `${props.idPrefix}-${role}`;
          return (
            <div key={role} className="flex items-center justify-between gap-3 px-3 py-2">
              <label htmlFor={id} className="text-sm text-text">
                {roleLabel(role)}
              </label>
              {props.kind === "folder" ? (
                <Select
                  value={props.value[role]}
                  onValueChange={(v) => props.onChange({ ...props.value, [role]: v as AccessLevel })}
                >
                  <SelectTrigger id={id} className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESS_LEVELS.map((level) => (
                      <SelectItem key={level} value={level} description={levelText(level).description}>
                        {levelText(level).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={props.value[role]}
                  onValueChange={(v) => props.onChange({ ...props.value, [role]: v as AccessChoice })}
                >
                  <SelectTrigger id={id} className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="folder" description="Follows the folder, and changes when the folder does.">
                      {/* A field that cannot be edited here reads the folder's "edit" as read only. */}
                      Same as folder ({levelText(props.platform && props.folder[role] === "edit" ? "read" : props.folder[role]).label.toLowerCase()})
                    </SelectItem>
                    {fieldLevels(Boolean(props.platform), props.value[role]).map((level) => (
                      <SelectItem key={level} value={level} description={levelText(level).description}>
                        {levelText(level).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-text-muted">
        {props.kind === "folder"
          ? "Applies to every field in the folder that has no setting of its own. Hidden fields are never sent to that role."
          : props.platform
            ? "The record's own cards, the contacts list and the search show this field to everyone who can open the record. These settings reach only the form it is listed on."
            : "A field's own setting wins over its folder's. Hidden fields are never sent to that role; the server enforces it."}
      </p>
    </fieldset>
  );
}

/** The words a table's badge uses: one per level, short enough for two roles to sit side by side. */
const SHORT: Record<AccessLevel, string> = { edit: "Edit", read: "Read", hidden: "Hidden" };

/** A compact "Sup: Edit · Rep: Hidden" for the tables; the full words are its tooltip and what a
 *  screen reader says. */
export function AccessSummary({
  levels, inherited,
}: {
  levels: Record<AccessRole, AccessLevel>;
  /** Roles whose level comes from the folder, shown quieter. */
  inherited?: Partial<Record<AccessRole, boolean>>;
}) {
  return (
    <span className="inline-flex gap-1 whitespace-nowrap">
      {ACCESS_ROLES.map((role) => {
        const said = `${roleLabel(role)}: ${levelText(levels[role]).label.toLowerCase()}${inherited?.[role] ? ", from the folder" : ""}`;
        return (
          <Badge
            key={role}
            size="sm"
            tone={levels[role] === "edit" ? "accent" : levels[role] === "hidden" ? "warning" : "neutral"}
            title={said}
            aria-label={said}
            className={inherited?.[role] ? "opacity-70" : undefined}
          >
            {role === "supervisor" ? "Sup" : "Rep"}: {SHORT[levels[role]]}
          </Badge>
        );
      })}
    </span>
  );
}
